import React, { useEffect, useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEdit, 
  faSearch, 
  faCalendarAlt, 
  faEye,
  faSort,
  faSortUp,
  faSortDown,
  faFilter,
  faTimes,
  faChevronDown
} from '@fortawesome/free-solid-svg-icons';
import { 
  Container, 
  TextField, 
  Button, 
  Table, 
  TableHead, 
  TableBody, 
  TableCell, 
  TableRow, 
  Pagination,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  Checkbox,
  ListItemText,
  OutlinedInput,
  FormGroup,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  Divider
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';

// Componente estilizado para los botones
const StyledButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
  color: 'white',
  height: '56px',
  fontWeight: 'bold',
  '&:hover': {
    background: 'linear-gradient(45deg, #1976D2 30%, #1EA7D2 90%)',
  },
}));

// Componente estilizado para las tarjetas
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
  },
}));

// Componente estilizado para celdas ordenables
const SortableTableCell = styled(TableCell)(({ theme }) => ({
  cursor: 'pointer',
  userSelect: 'none',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
}));

// Función para normalizar fecha a formato dd/mm/yyyy
const normalizeFecha = (fechaString) => {
  if (!fechaString || fechaString.trim() === '') return '';
  
  try {
    const fechaStr = fechaString.toString().trim();
    
    // Si ya está en formato dd/mm/yyyy, devolverlo
    if (fechaStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      return fechaStr;
    }
    
    // Si está en formato yyyy-mm-dd
    if (fechaStr.match(/^\d{4}-\d{2}-\d{2}/)) {
      const [year, month, day] = fechaStr.split('-');
      return `${day}/${month}/${year}`;
    }
    
    // Si tiene hora incluida (yyyy-mm-ddThh:mm:ss)
    if (fechaStr.match(/^\d{4}-\d{2}-\d{2}T/)) {
      const [datePart] = fechaStr.split('T');
      const [year, month, day] = datePart.split('-');
      return `${day}/${month}/${year}`;
    }
    
    // Si tiene hora incluida con espacio (yyyy-mm-dd hh:mm:ss)
    if (fechaStr.match(/^\d{4}-\d{2}-\d{2}\s/)) {
      const [datePart] = fechaStr.split(' ');
      const [year, month, day] = datePart.split('-');
      return `${day}/${month}/${year}`;
    }
    
    // Intentar crear objeto Date
    let fechaObj;
    
    // Si tiene formato dd/mm/yyyy con hora
    if (fechaStr.match(/^\d{2}\/\d{2}\/\d{4}/)) {
      const [datePart] = fechaStr.split(' ');
      const [day, month, year] = datePart.split('/');
      fechaObj = new Date(`${year}-${month}-${day}`);
    } else {
      // Intentar parsear directamente
      fechaObj = new Date(fechaStr);
    }
    
    if (!isNaN(fechaObj.getTime())) {
      const day = String(fechaObj.getDate()).padStart(2, '0');
      const month = String(fechaObj.getMonth() + 1).padStart(2, '0');
      const year = fechaObj.getFullYear();
      return `${day}/${month}/${year}`;
    }
    
    // Si no se pudo parsear, devolver el string original
    return fechaStr;
  } catch (error) {
    console.error('Error al normalizar fecha:', fechaString, error);
    return fechaString;
  }
};

// Función para convertir de dd/mm/yyyy a yyyy-mm-dd para enviar al servidor
const fechaParaServidor = (fechaString) => {
  if (!fechaString) return '';
  
  try {
    // Si ya está en formato yyyy-mm-dd
    if (fechaString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return fechaString;
    }
    
    // Si está en formato dd/mm/yyyy
    if (fechaString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const [day, month, year] = fechaString.split('/');
      return `${year}-${month}-${day}`;
    }
    
    return fechaString;
  } catch (error) {
    return fechaString;
  }
};

// Función para convertir fecha de input date a string dd/mm/yyyy
const formatDateForDisplay = (dateString) => {
  if (!dateString) return '';
  // Asume que dateString viene en formato yyyy-mm-dd del input date
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

// Función para comparar fechas en formato yyyy-mm-dd
const compararFechas = (fechaItem, fechaInicio, fechaFin) => {
  if (!fechaItem) return false;
  
  // Convertir fechaItem de dd/mm/yyyy a yyyy-mm-dd para comparar
  let fechaItemYMD = fechaItem;
  if (fechaItem.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
    const [day, month, year] = fechaItem.split('/');
    fechaItemYMD = `${year}-${month}-${day}`;
  }
  
  if (fechaInicio && fechaItemYMD < fechaInicio) return false;
  if (fechaFin && fechaItemYMD > fechaFin) return false;
  
  return true;
};

const MoneyFormatter = ({ value }) => {
  const formatter = new Intl.NumberFormat('es-GT', {
    style: 'currency',
    currency: 'GTQ',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  const formattedValue = formatter.format(parseFloat(value) || 0);

  return <span>{formattedValue}</span>;
};

// Función para normalizar texto para búsquedas
const normalizeText = (text) => {
  if (!text) return '';
  return text.toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
};

// Estilos para el select múltiple
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function ListaOM() {
  const [datosGenerados, setDatosGenerados] = useState([]);
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState(""); 
  const [filtroTexto, setFiltroTexto] = useState("");
  const [paginaActual, setPaginaActual] = useState(1);
  const [elementosPorPagina, setElementosPorPagina] = useState(100);
  
  // Estados para ordenamiento
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'ascending'
  });
  
  // NUEVOS ESTADOS PARA FILTROS PRINCIPALES
  const [filtrosPrincipales, setFiltrosPrincipales] = useState({
    Pagado: [], // Array para multiselect
    FechaEntrega: {
      inicio: '', // Formato yyyy-mm-dd
      fin: ''
    },
    CondicionPago: [], // Array para multiselect
    Total: 'todos' // 'todos', 'menorIgual500', 'mayor500'
  });
  
  // Estados para opciones de los combos
  const [opcionesPagado, setOpcionesPagado] = useState([]);
  const [opcionesCondicionPago, setOpcionesCondicionPago] = useState([]);
  
  // Estados para filtros avanzados
  const [filtrosAvanzados, setFiltrosAvanzados] = useState({
    Codigo: '',
    CodCliente: '',
    NombreCliente: '',
    Vendedor: '',
    Telefono: '',
    Estado: ''
  });
  
  // Estados para UI
  const [filtrosActivos, setFiltrosActivos] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Función para obtener la fecha de inicio según la regla (devuelve formato yyyy-mm-dd)
  const obtenerFechaInicio = () => {
    const hoy = new Date();
    const dia = hoy.getDate();
    const mes = hoy.getMonth();
    const año = hoy.getFullYear();
    
    if (dia <= 10) {
      const fechaInicioMes = new Date(año, mes - 1, 1);
      return fechaInicioMes.toISOString().split('T')[0];
    } else {
      const fechaInicioMes = new Date(año, mes, 1);
      return fechaInicioMes.toISOString().split('T')[0];
    }
  };

  // Función para obtener la fecha actual (devuelve formato yyyy-mm-dd)
  const obtenerFechaFin = () => {
    const hoy = new Date();
    return hoy.toISOString().split('T')[0];
  };

  // Inicializar fechas al cargar el componente
  useEffect(() => {
    const fechaInicial = obtenerFechaInicio();
    const fechaFinal = obtenerFechaFin();
    
    setFechaInicio(fechaInicial);
    setFechaFin(fechaFinal);
  }, []);

  // Efecto para actualizar filtros activos
  useEffect(() => {
    const nuevosFiltros = [];
    
    // Filtros principales
    if (filtrosPrincipales.Pagado.length > 0) {
      nuevosFiltros.push({
        key: 'Pagado',
        value: filtrosPrincipales.Pagado.join(', '),
        label: 'Pagado'
      });
    }
    
    if (filtrosPrincipales.FechaEntrega.inicio || filtrosPrincipales.FechaEntrega.fin) {
      let fechaTexto = '';
      if (filtrosPrincipales.FechaEntrega.inicio && filtrosPrincipales.FechaEntrega.fin) {
        fechaTexto = `${formatDateForDisplay(filtrosPrincipales.FechaEntrega.inicio)} - ${formatDateForDisplay(filtrosPrincipales.FechaEntrega.fin)}`;
      } else if (filtrosPrincipales.FechaEntrega.inicio) {
        fechaTexto = `Desde ${formatDateForDisplay(filtrosPrincipales.FechaEntrega.inicio)}`;
      } else if (filtrosPrincipales.FechaEntrega.fin) {
        fechaTexto = `Hasta ${formatDateForDisplay(filtrosPrincipales.FechaEntrega.fin)}`;
      }
      nuevosFiltros.push({
        key: 'FechaEntrega',
        value: fechaTexto,
        label: 'Fecha Entrega'
      });
    }
    
    if (filtrosPrincipales.CondicionPago.length > 0) {
      nuevosFiltros.push({
        key: 'CondicionPago',
        value: filtrosPrincipales.CondicionPago.join(', '),
        label: 'Condición Pago'
      });
    }
    
    if (filtrosPrincipales.Total !== 'todos') {
      const totalTexto = filtrosPrincipales.Total === 'menorIgual500' ? '≤ Q500' : '> Q500';
      nuevosFiltros.push({
        key: 'Total',
        value: totalTexto,
        label: 'Total'
      });
    }
    
    // Filtros avanzados
    Object.entries(filtrosAvanzados).forEach(([key, value]) => {
      if (value && value.toString().trim() !== '') {
        nuevosFiltros.push({
          key,
          value,
          label: obtenerLabelFiltro(key)
        });
      }
    });
    
    setFiltrosActivos(nuevosFiltros);
  }, [filtrosPrincipales, filtrosAvanzados]);

  // Función para obtener label de filtro
  const obtenerLabelFiltro = (key) => {
    const labels = {
      Codigo: 'Código',
      CodCliente: 'Código Cliente',
      NombreCliente: 'Nombre Cliente',
      Vendedor: 'Vendedor',
      Telefono: 'Teléfono',
      Estado: 'Estado'
    };
    return labels[key] || key;
  };

  // Función para extraer opciones únicas de los datos
  const extraerOpcionesUnicas = (datos) => {
    // Extraer valores únicos para Pagado (normalizados)
    const valoresPagado = [...new Set(datos.map(item => {
      const valor = item.Pagado?.toString().trim();
      return valor || 'No';
    }))];
    setOpcionesPagado(valoresPagado.sort());
    
    // Extraer valores únicos para CondicionPago
    const valoresCondicionPago = [...new Set(datos.map(item => {
      return item.CondicionPago_Conta?.toString().trim() || 'Sin condición';
    }))];
    setOpcionesCondicionPago(valoresCondicionPago.sort());
  };

  const fetchData = () => {
    // Convertir fechas a formato yyyy-mm-dd para el servidor
    const fechaInicioServidor = fechaParaServidor(fechaInicio);
    const fechaFinServidor = fechaParaServidor(fechaFin);
    
    const datos = {
      Token: "LJKNFUH2FWEJBAEFFFFFKJ VGDEA003CSFCAQWEUIRRRRRRRRRRRADFYGBIFHVBESRNYHIOPWJROTY4WY",
      Ambiente: "DESA",
      Dispositivo: {
        Marca: "SAMSUNG",
        Modelo: "S22002B",
        Version: "Android 18.125",
        McAdress: null,
        PowerStatus: "84%",
        WifiStatus: "Conected to iphone de Mauricio",
        GPSStatus: "ON",
        GPS: "321|-485|321"
      },
      Usr: {
        Codigo: "",
        UserNickName: "pierril"
      },
      DatosMetodo: {
        NombreMetodo: "ListaOM",
        Datos_Recibidos: `{"FECHAINI":"${fechaInicioServidor}","FECHAFIN":"${fechaFinServidor}"}`
      }
    };

    const fetchDataUrl = `https://appcorp.parma.com.gt/API/ReceptorDatos?Datos=${encodeURIComponent(JSON.stringify(datos))}`;
    
    fetch(fetchDataUrl)
      .then(response => response.json())
      .then(data => {
        const datosGeneradosDecodificados = JSON.parse(data.datos_Generados);
        console.log(datosGeneradosDecodificados);
        const datosFormateados = datosGeneradosDecodificados.map(item => ({
          ...item,
          Fechacreacion: normalizeFecha(item.Fechacreacion),
          Fecha_entrega: normalizeFecha(item.Fecha_entrega)
        }));
        
        setDatosGenerados(datosFormateados);
        extraerOpcionesUnicas(datosFormateados);
      })
      .catch(error => console.error('Error:', error));
  };

  useEffect(() => {
    if (fechaInicio && fechaFin) {
      fetchData();
    }
  }, [fechaInicio, fechaFin]);

  const handleSearch = () => {
    fetchData();
  };

  const handlePageChange = (event, value) => {
    setPaginaActual(value);
  };

  const handleElementosPorPaginaChange = (event) => {
    setElementosPorPagina(event.target.value);
    setPaginaActual(1);
  };

  // Función para manejar cambio en fecha de inicio
  const handleFechaInicioChange = (e) => {
    const value = e.target.value;
    if (value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const [day, month, year] = value.split('/');
      setFechaInicio(`${year}-${month}-${day}`);
    } else {
      setFechaInicio(value);
    }
  };

  // Función para manejar cambio en fecha fin
  const handleFechaFinChange = (e) => {
    const value = e.target.value;
    if (value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const [day, month, year] = value.split('/');
      setFechaFin(`${year}-${month}-${day}`);
    } else {
      setFechaFin(value);
    }
  };

  // Funciones para manejar filtros principales
  const handlePagadoChange = (event) => {
    const {
      target: { value },
    } = event;
    setFiltrosPrincipales(prev => ({
      ...prev,
      Pagado: typeof value === 'string' ? value.split(',') : value,
    }));
    setPaginaActual(1);
  };

  const handleFechaEntregaInicioChange = (e) => {
    setFiltrosPrincipales(prev => ({
      ...prev,
      FechaEntrega: {
        ...prev.FechaEntrega,
        inicio: e.target.value
      }
    }));
    setPaginaActual(1);
  };

  const handleFechaEntregaFinChange = (e) => {
    setFiltrosPrincipales(prev => ({
      ...prev,
      FechaEntrega: {
        ...prev.FechaEntrega,
        fin: e.target.value
      }
    }));
    setPaginaActual(1);
  };

  const handleCondicionPagoChange = (event) => {
    const {
      target: { value },
    } = event;
    setFiltrosPrincipales(prev => ({
      ...prev,
      CondicionPago: typeof value === 'string' ? value.split(',') : value,
    }));
    setPaginaActual(1);
  };

  const handleTotalChange = (event) => {
    setFiltrosPrincipales(prev => ({
      ...prev,
      Total: event.target.value
    }));
    setPaginaActual(1);
  };

  // Función de ordenamiento mejorada
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    } else if (sortConfig.key === key && sortConfig.direction === 'descending') {
      direction = null;
    }
    
    setSortConfig(direction ? { key, direction } : { key: null, direction: 'ascending' });
  };

  // Función para obtener datos ordenados
  const getSortedData = (data) => {
    if (!sortConfig.key) return data;
    
    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';
      
      if (sortConfig.key === 'Fechacreacion' || sortConfig.key === 'Fecha_entrega') {
        try {
          const convertirFecha = (fechaStr) => {
            if (!fechaStr) return new Date(0);
            if (fechaStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
              const [day, month, year] = fechaStr.split('/');
              return new Date(`${year}-${month}-${day}`);
            }
            return new Date(fechaStr);
          };
          
          const aDate = convertirFecha(aValue);
          const bDate = convertirFecha(bValue);
          
          if (aDate < bDate) {
            return sortConfig.direction === 'ascending' ? -1 : 1;
          }
          if (aDate > bDate) {
            return sortConfig.direction === 'ascending' ? 1 : -1;
          }
          return 0;
        } catch (error) {
          console.warn('Error al ordenar fecha:', error);
        }
      }
      
      if (sortConfig.key === 'Total') {
        const aNum = parseFloat(aValue) || 0;
        const bNum = parseFloat(bValue) || 0;
        
        if (aNum < bNum) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aNum > bNum) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      }
      
      if (aValue < bValue) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
  };

  // Función para manejar cambio en filtros avanzados
  const handleFiltroAvanzadoChange = (key, value) => {
    setFiltrosAvanzados(prev => ({
      ...prev,
      [key]: value
    }));
    setPaginaActual(1);
  };

  // Función para limpiar un filtro específico
  const handleClearFiltro = (key) => {
    if (key === 'Pagado') {
      setFiltrosPrincipales(prev => ({ ...prev, Pagado: [] }));
    } else if (key === 'FechaEntrega') {
      setFiltrosPrincipales(prev => ({ 
        ...prev, 
        FechaEntrega: { inicio: '', fin: '' } 
      }));
    } else if (key === 'CondicionPago') {
      setFiltrosPrincipales(prev => ({ ...prev, CondicionPago: [] }));
    } else if (key === 'Total') {
      setFiltrosPrincipales(prev => ({ ...prev, Total: 'todos' }));
    } else {
      setFiltrosAvanzados(prev => ({ ...prev, [key]: '' }));
    }
  };

  // Función para limpiar todos los filtros
  const handleClearAllFiltros = () => {
    setFiltrosPrincipales({
      Pagado: [],
      FechaEntrega: {
        inicio: '',
        fin: ''
      },
      CondicionPago: [],
      Total: 'todos'
    });
    
    setFiltrosAvanzados({
      Codigo: '',
      CodCliente: '',
      NombreCliente: '',
      Vendedor: '',
      Telefono: '',
      Estado: ''
    });
    
    setFiltroTexto('');
  };

  // Función mejorada para filtrar por texto
  const filtrarPorTexto = (item, textoBusqueda) => {
    if (!textoBusqueda.trim()) return true;
    
    const busquedaNormalizada = normalizeText(textoBusqueda);
    const palabrasBusqueda = busquedaNormalizada.split(/\s+/).filter(palabra => palabra.length > 0);
    
    if (palabrasBusqueda.length === 0) return true;
    
    return palabrasBusqueda.every(palabra => {
      const columnas = [
        item.Codigo,
        item.Fechacreacion,
        item.Fecha_entrega,
        item.Total,
        item.CondicionPago_Conta,
        item.CodCliente,
        item.NombreCliente,
        item.Vendedor,
        item.Telefono,
        item.Estado,
        item.Pagado
      ];
      
      return columnas.some(valor => {
        if (!valor) return false;
        return normalizeText(valor).includes(palabra);
      });
    });
  };

  // Función para filtrar por filtros principales
  const filtrarPorFiltrosPrincipales = (item) => {
    // Filtro Pagado (multiselect)
    if (filtrosPrincipales.Pagado.length > 0) {
      const valorItem = item.Pagado?.toString().trim() || 'No';
      if (!filtrosPrincipales.Pagado.includes(valorItem)) {
        return false;
      }
    }
    
    // Filtro Fecha Entrega (rango)
    if (filtrosPrincipales.FechaEntrega.inicio || filtrosPrincipales.FechaEntrega.fin) {
      if (!compararFechas(
        item.Fecha_entrega,
        filtrosPrincipales.FechaEntrega.inicio,
        filtrosPrincipales.FechaEntrega.fin
      )) {
        return false;
      }
    }
    
    // Filtro Condicion Pago (multiselect)
    if (filtrosPrincipales.CondicionPago.length > 0) {
      const valorItem = item.CondicionPago_Conta?.toString().trim() || 'Sin condición';
      if (!filtrosPrincipales.CondicionPago.includes(valorItem)) {
        return false;
      }
    }
    
    // Filtro Total
    if (filtrosPrincipales.Total !== 'todos') {
      const totalItem = parseFloat(item.Total) || 0;
      if (filtrosPrincipales.Total === 'menorIgual500' && totalItem > 500) {
        return false;
      }
      if (filtrosPrincipales.Total === 'mayor500' && totalItem <= 500) {
        return false;
      }
    }
    
    return true;
  };

  // Función para filtrar por filtros avanzados
  const filtrarPorFiltrosAvanzados = (item) => {
    return Object.entries(filtrosAvanzados).every(([key, filterValue]) => {
      if (!filterValue || filterValue.toString().trim() === '') return true;
      
      const itemValue = item[key];
      if (itemValue === null || itemValue === undefined) return false;
      
      const valorNormalizado = normalizeText(itemValue);
      const filtroNormalizado = normalizeText(filterValue);
      
      return valorNormalizado.includes(filtroNormalizado);
    });
  };

  // Filtrar datos
  const filteredData = useMemo(() => {
    return datosGenerados.filter(item => {
      const pasaFiltroTexto = filtrarPorTexto(item, filtroTexto);
      const pasaFiltrosPrincipales = filtrarPorFiltrosPrincipales(item);
      const pasaFiltrosAvanzados = filtrarPorFiltrosAvanzados(item);
      
      return pasaFiltroTexto && pasaFiltrosPrincipales && pasaFiltrosAvanzados;
    });
  }, [datosGenerados, filtroTexto, filtrosPrincipales, filtrosAvanzados]);

  // Ordenar datos
  const sortedData = getSortedData(filteredData);
  
  // Paginar datos
  const indexUltimoElemento = paginaActual * elementosPorPagina;
  const indexPrimerElemento = indexUltimoElemento - elementosPorPagina;
  const datosPaginados = sortedData.slice(indexPrimerElemento, indexUltimoElemento);

  // Opciones para elementos por página
  const opcionesElementos = [100, 200, 300, 400, 500, 600, 700, 800, 900, 1000];

  // Obtener icono de ordenamiento
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return faSort;
    return sortConfig.direction === 'ascending' ? faSortUp : faSortDown;
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography 
        variant="h4" 
        component="h1" 
        gutterBottom 
        sx={{ 
          fontWeight: 'bold',
          color: '#2c3e50',
          mb: 4
        }}
      >
        Lista de Órdenes de Mantenimiento
      </Typography>

      <StyledCard sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={3}>
            {/* Buscador principal */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                id="filtroTexto"
                label="Buscar en todas las columnas"
                value={filtroTexto}
                onChange={(e) => setFiltroTexto(e.target.value)}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FontAwesomeIcon icon={faSearch} />
                    </InputAdornment>
                  ),
                  endAdornment: filtroTexto && (
                    <InputAdornment position="end">
                      <IconButton 
                        size="small" 
                        onClick={() => setFiltroTexto('')}
                        edge="end"
                      >
                        <FontAwesomeIcon icon={faTimes} size="xs" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                placeholder="Ej: COD123 Juan Pérez"
                helperText="Busca por palabras separadas por espacio"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                  }
                }}
              />
            </Grid>

            {/* Botón filtros avanzados */}
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setDialogOpen(true)}
                startIcon={<FontAwesomeIcon icon={faFilter} />}
                sx={{ 
                  height: '56px',
                  borderRadius: '12px'
                }}
              >
                Filtros Avanzados
                {filtrosActivos.filter(f => !['Pagado', 'FechaEntrega', 'CondicionPago', 'Total'].includes(f.key)).length > 0 && (
                  <Chip 
                    label={filtrosActivos.filter(f => !['Pagado', 'FechaEntrega', 'CondicionPago', 'Total'].includes(f.key)).length} 
                    size="small" 
                    color="primary"
                    sx={{ ml: 1, height: '20px' }}
                  />
                )}
              </Button>
            </Grid>

            {/* Configuración de elementos por página */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="elementos-por-pagina-label">Elementos por página</InputLabel>
                <Select
                  labelId="elementos-por-pagina-label"
                  value={elementosPorPagina}
                  label="Elementos por página"
                  onChange={handleElementosPorPaginaChange}
                  sx={{ borderRadius: '12px' }}
                >
                  {opcionesElementos.map((numero) => (
                    <MenuItem key={numero} value={numero}>
                      {numero} elementos
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Fechas y botón de búsqueda */}
            <Grid item xs={12} md={3}>
              <Box display="flex" gap={2}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setFechaInicio(obtenerFechaInicio());
                    setFechaFin(obtenerFechaFin());
                  }}
                  startIcon={<FontAwesomeIcon icon={faCalendarAlt} />}
                  sx={{ borderRadius: '12px' }}
                >
                  Reset
                </Button>
                <StyledButton
                  variant="contained"
                  onClick={handleSearch}
                  startIcon={<FontAwesomeIcon icon={faSearch} />}
                >
                  Buscar
                </StyledButton>
              </Box>
            </Grid>

            {/* Selectores de fecha de rango principal */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="fechaInicio"
                label="Fecha de Inicio (dd/mm/yyyy)"
                type="date"
                value={fechaInicio}
                onChange={handleFechaInicioChange}
                InputLabelProps={{
                  shrink: true,
                }}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="fechaFin"
                label="Fecha de Fin (dd/mm/yyyy)"
                type="date"
                value={fechaFin}
                onChange={handleFechaFinChange}
                InputLabelProps={{
                  shrink: true,
                }}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                  }
                }}
              />
            </Grid>

            {/* NUEVOS FILTROS PRINCIPALES */}
            
            {/* Filtro Pagado - Multiselect */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="pagado-label">Pagado</InputLabel>
                <Select
                  labelId="pagado-label"
                  multiple
                  value={filtrosPrincipales.Pagado}
                  onChange={handlePagadoChange}
                  input={<OutlinedInput label="Pagado" />}
                  renderValue={(selected) => selected.join(', ')}
                  MenuProps={MenuProps}
                  sx={{ borderRadius: '12px' }}
                >
                  {opcionesPagado.map((opcion) => (
                    <MenuItem key={opcion} value={opcion}>
                      <Checkbox checked={filtrosPrincipales.Pagado.indexOf(opcion) > -1} />
                      <ListItemText primary={opcion} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Filtro Fecha Entrega - Rango con inputs nativos */}
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Fecha Entrega Desde"
                    type="date"
                    value={filtrosPrincipales.FechaEntrega.inicio}
                    onChange={handleFechaEntregaInicioChange}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    size="medium"
                    sx={{ borderRadius: '12px' }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Fecha Entrega Hasta"
                    type="date"
                    value={filtrosPrincipales.FechaEntrega.fin}
                    onChange={handleFechaEntregaFinChange}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    size="medium"
                    sx={{ borderRadius: '12px' }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Filtro Condicion Pago - Multiselect */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="condicion-pago-label">Condición de Pago</InputLabel>
                <Select
                  labelId="condicion-pago-label"
                  multiple
                  value={filtrosPrincipales.CondicionPago}
                  onChange={handleCondicionPagoChange}
                  input={<OutlinedInput label="Condición de Pago" />}
                  renderValue={(selected) => selected.join(', ')}
                  MenuProps={MenuProps}
                  sx={{ borderRadius: '12px' }}
                >
                  {opcionesCondicionPago.map((opcion) => (
                    <MenuItem key={opcion} value={opcion}>
                      <Checkbox checked={filtrosPrincipales.CondicionPago.indexOf(opcion) > -1} />
                      <ListItemText primary={opcion} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Filtro Total - Radio buttons */}
            <Grid item xs={12} md={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Total</FormLabel>
                <RadioGroup
                  row
                  value={filtrosPrincipales.Total}
                  onChange={handleTotalChange}
                >
                  <FormControlLabel value="todos" control={<Radio />} label="Todos" />
                  <FormControlLabel value="menorIgual500" control={<Radio />} label="Menor o Igual a Q500" />
                  <FormControlLabel value="mayor500" control={<Radio />} label="Mayor a Q500" />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>

          {/* Filtros activos */}
          {filtrosActivos.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Filtros activos:
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {filtrosActivos.map((filtro) => (
                  <Chip
                    key={filtro.key}
                    label={`${filtro.label}: ${filtro.value}`}
                    onDelete={() => handleClearFiltro(filtro.key)}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                ))}
                {filtroTexto && (
                  <Chip
                    label={`Búsqueda: ${filtroTexto}`}
                    onDelete={() => setFiltroTexto('')}
                    color="secondary"
                    variant="outlined"
                    size="small"
                  />
                )}
                <Chip
                  label="Limpiar todos"
                  onClick={handleClearAllFiltros}
                  color="error"
                  variant="outlined"
                  size="small"
                />
              </Box>
            </Box>
          )}
        </CardContent>
      </StyledCard>

      {/* Información de resultados */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="body2" color="text.secondary">
          Mostrando {datosPaginados.length} de {filteredData.length} resultados
          {sortConfig.key && (
            <span style={{ marginLeft: '10px' }}>
              Ordenado por: {sortConfig.key} ({sortConfig.direction === 'ascending' ? 'Asc' : 'Desc'})
            </span>
          )}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Página {paginaActual} de {Math.ceil(filteredData.length / elementosPorPagina)}
        </Typography>
      </Box>

      {/* Tabla de datos */}
      <Paper 
        sx={{ 
          width: '100%', 
          overflow: 'hidden',
          borderRadius: '12px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
        }}
      >
        <Box sx={{ maxHeight: '600px', overflow: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f7fa' }}>
                <SortableTableCell 
                  onClick={() => requestSort('Codigo')}
                  sx={{ fontWeight: 'bold', color: '#34495e' }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    Código
                    <FontAwesomeIcon icon={getSortIcon('Codigo')} size="xs" />
                  </Box>
                </SortableTableCell>
                
                <SortableTableCell 
                  onClick={() => requestSort('Fechacreacion')}
                  sx={{ fontWeight: 'bold', color: '#34495e' }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    Fecha de Creación
                    <FontAwesomeIcon icon={getSortIcon('Fechacreacion')} size="xs" />
                  </Box>
                </SortableTableCell>
                
                <SortableTableCell 
                  onClick={() => requestSort('Fecha_entrega')}
                  sx={{ fontWeight: 'bold', color: '#34495e' }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    Fecha Entrega
                    <FontAwesomeIcon icon={getSortIcon('Fecha_entrega')} size="xs" />
                  </Box>
                </SortableTableCell>
                
                <SortableTableCell 
                  onClick={() => requestSort('Total')}
                  sx={{ fontWeight: 'bold', color: '#34495e' }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    Total
                    <FontAwesomeIcon icon={getSortIcon('Total')} size="xs" />
                  </Box>
                </SortableTableCell>

                <SortableTableCell 
                  onClick={() => requestSort('CondicionPago_Conta')}
                  sx={{ fontWeight: 'bold', color: '#34495e' }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    Condición de Pago
                    <FontAwesomeIcon icon={getSortIcon('CondicionPago_Conta')} size="xs" />
                  </Box>
                </SortableTableCell>

                <SortableTableCell 
                  onClick={() => requestSort('CodCliente')}
                  sx={{ fontWeight: 'bold', color: '#34495e' }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    Código del Cliente
                    <FontAwesomeIcon icon={getSortIcon('CodCliente')} size="xs" />
                  </Box>
                </SortableTableCell>
                
                <SortableTableCell 
                  onClick={() => requestSort('NombreCliente')}
                  sx={{ fontWeight: 'bold', color: '#34495e' }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    Nombre del Cliente
                    <FontAwesomeIcon icon={getSortIcon('NombreCliente')} size="xs" />
                  </Box>
                </SortableTableCell>
                
                <SortableTableCell 
                  onClick={() => requestSort('Vendedor')}
                  sx={{ fontWeight: 'bold', color: '#34495e' }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    Vendedor
                    <FontAwesomeIcon icon={getSortIcon('Vendedor')} size="xs" />
                  </Box>
                </SortableTableCell>
                
                <SortableTableCell 
                  onClick={() => requestSort('Telefono')}
                  sx={{ fontWeight: 'bold', color: '#34495e' }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    Teléfono
                    <FontAwesomeIcon icon={getSortIcon('Telefono')} size="xs" />
                  </Box>
                </SortableTableCell>
                
                <SortableTableCell 
                  onClick={() => requestSort('Estado')}
                  sx={{ fontWeight: 'bold', color: '#34495e' }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    Estado
                    <FontAwesomeIcon icon={getSortIcon('Estado')} size="xs" />
                  </Box>
                </SortableTableCell>
                
                <TableCell sx={{ fontWeight: 'bold', color: '#34495e' }}>
                  Acciones
                </TableCell>
                
                <SortableTableCell 
                  onClick={() => requestSort('Pagado')}
                  sx={{ fontWeight: 'bold', color: '#34495e' }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    Pagado
                    <FontAwesomeIcon icon={getSortIcon('Pagado')} size="xs" />
                  </Box>
                </SortableTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {datosPaginados.map((item, index) => (
                <TableRow 
                  key={item.Codigo || index}
                  sx={{ 
                    '&:hover': { backgroundColor: '#f8f9fa' },
                    '&:nth-of-type(even)': { backgroundColor: '#fafbfc' }
                  }}
                >
                  <TableCell>{item.Codigo || '-'}</TableCell>
                  <TableCell>{item.Fechacreacion || '-'}</TableCell>
                  <TableCell>{item.Fecha_entrega || '-'}</TableCell>
                  <TableCell><MoneyFormatter value={item.Total || '0'} /></TableCell>
                  <TableCell>{item.CondicionPago_Conta || '-'}</TableCell>
                  <TableCell>{item.CodCliente || '-'}</TableCell>
                  <TableCell>{item.NombreCliente || '-'}</TableCell>
                  <TableCell>{item.Vendedor || '-'}</TableCell>
                  <TableCell>{item.Telefono || '-'}</TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: 'inline-block',
                        px: 1,
                        py: 0.5,
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        fontWeight: 'medium',
                        backgroundColor: item.Estado === 'Completado' ? '#e8f5e9' : 
                                       item.Estado === 'Pendiente' ? '#fff3e0' : '#f5f5f5',
                        color: item.Estado === 'Completado' ? '#2e7d32' : 
                               item.Estado === 'Pendiente' ? '#f57c00' : '#757575'
                      }}
                    >
                      {item.Estado || '-'}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Ver detalles">
                      <Button
                        variant="outlined"
                        size="small"
                        href={`detalleom/${item.Codigo}`}
                        startIcon={<FontAwesomeIcon icon={faEye} />}
                        sx={{ 
                          borderRadius: '8px',
                          textTransform: 'none',
                          borderColor: '#4CAF50',
                          color: '#4CAF50',
                          '&:hover': {
                            backgroundColor: '#E8F5E9',
                            borderColor: '#2E7D32'
                          }
                        }}
                      >
                        Ver
                      </Button>
                    </Tooltip>
                  </TableCell> 
                  <TableCell>
                    <Box
                      sx={{
                        display: 'inline-block',
                        px: 1,
                        py: 0.5,
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        fontWeight: 'medium',
                        backgroundColor: 
                          item.Pagado === 'Sí' || normalizeText(item.Pagado) === 'si' ? '#e8f5e9' : 
                          item.Pagado === 'CARGADO' || normalizeText(item.Pagado) === 'cargado' ? '#fff3e0' : 
                          '#ffebee',
                        color: 
                          item.Pagado === 'Sí' || normalizeText(item.Pagado) === 'si' ? '#2e7d32' : 
                          item.Pagado === 'CARGADO' || normalizeText(item.Pagado) === 'cargado' ? '#ff6f00' : 
                          '#c62828'
                      }}
                    >
                      {item.Pagado || 'No'}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      {/* Paginación */}
      <Box display="flex" justifyContent="center" mt={4}>
        <Pagination
          count={Math.ceil(filteredData.length / elementosPorPagina)}
          page={paginaActual}
          onChange={handlePageChange}
          color="primary"
          size="large"
          showFirstButton
          showLastButton
          sx={{
            '& .MuiPaginationItem-root': {
              borderRadius: '8px',
              margin: '0 4px',
            }
          }}
        />
      </Box>

      {/* Diálogo de filtros avanzados - REDUCIDO */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Filtros Avanzados</Typography>
            <IconButton onClick={() => setDialogOpen(false)} size="small">
              <FontAwesomeIcon icon={faTimes} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Código"
                value={filtrosAvanzados.Codigo}
                onChange={(e) => handleFiltroAvanzadoChange('Codigo', e.target.value)}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Código Cliente"
                value={filtrosAvanzados.CodCliente}
                onChange={(e) => handleFiltroAvanzadoChange('CodCliente', e.target.value)}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nombre Cliente"
                value={filtrosAvanzados.NombreCliente}
                onChange={(e) => handleFiltroAvanzadoChange('NombreCliente', e.target.value)}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Vendedor"
                value={filtrosAvanzados.Vendedor}
                onChange={(e) => handleFiltroAvanzadoChange('Vendedor', e.target.value)}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Teléfono"
                value={filtrosAvanzados.Telefono}
                onChange={(e) => handleFiltroAvanzadoChange('Telefono', e.target.value)}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Estado</InputLabel>
                <Select
                  value={filtrosAvanzados.Estado}
                  label="Estado"
                  onChange={(e) => handleFiltroAvanzadoChange('Estado', e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="Completado">Completado</MenuItem>
                  <MenuItem value="Pendiente">Pendiente</MenuItem>
                  <MenuItem value="En Proceso">En Proceso</MenuItem>
                  <MenuItem value="Cancelado">Cancelado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleClearAllFiltros}
            color="error"
            startIcon={<FontAwesomeIcon icon={faTimes} />}
          >
            Limpiar Filtros
          </Button>
          <Button 
            onClick={() => setDialogOpen(false)}
            color="primary"
            variant="contained"
          >
            Aplicar Filtros
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default ListaOM;